import type { MaxRentHistoryModalProps, MaxRentHistoryRow } from '../../types/modals'
import { useCallback, useEffect, useState } from 'react'
import { ApplicationModalLoadingState } from '../ApplicationListLoadingState'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { getMaxRentHistory } from '../../services/applicationService'
import { showError } from '@/lib/utils/notifications'
import { formatAmount } from '@/lib/utils/formatters'
import {
  formatMaxRentHistoryCell,
  formatMaxRentHistoryItrType,
} from '../../utils/applicationFormatters'



const tid = 'application-modal-max-rent-history'

function formatMaxRentColumn(value: unknown): string {
  if (value == null || value === '') return '—'
  const num =
    typeof value === 'number'
      ? value
      : Number(String(value).replace(/,/g, '').trim())
  if (Number.isFinite(num)) return formatAmount(num)
  return String(value)
}

export function MaxRentHistoryModal({ isOpen, onClose, applicationId }: MaxRentHistoryModalProps) {
  const [rows, setRows] = useState<MaxRentHistoryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)

  const load = useCallback(async () => {
    if (applicationId == null) return
    setLoading(true)
    try {
      const res = await getMaxRentHistory(applicationId, {
        page,
        perPage: 10,
        sortBy: 'desc',
        orderBy: 'id',
      })
      const raw = Array.isArray(res.data) ? res.data : []
      setRows(raw.filter((r): r is MaxRentHistoryRow => r != null && typeof r === 'object'))
      setLastPage(res.last ?? 1)
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load max rent history.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [applicationId, page])

  useEffect(() => {
    if (!isOpen || applicationId == null) return
    void load()
  }, [isOpen, applicationId, load])

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Max Rent History"
      onClose={onClose}
      maxWidth="max-w-3xl"
      bodyScrollOnly
      headerVariant="centeredTitle"
      headerClassName="bg-gradient-to-r from-blue-100 via-sky-50 to-blue-100"
      bodyClassName="!p-5 bg-white"
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
    >
      {loading ? (
        <ApplicationModalLoadingState testId={`${tid}-loading`} className="min-h-32" />
      ) : rows.length === 0 ? (
        <div
          className="flex min-h-32 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm font-medium text-slate-500"
          data-testid={`${tid}-empty`}
        >
          No records found.
        </div>
      ) : (
        <div
          className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_25px_rgba(15,23,42,0.08)]"
          data-testid={`${tid}-table-container`}
        >
          <div className="overflow-x-auto">
          <table className="min-w-[620px] w-full border-collapse text-[13px]" data-testid={`${tid}-table`}>
            <thead data-testid={`${tid}-table-head`}>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs tracking-wide text-slate-950">
                <th className="px-4 py-3 font-bold whitespace-nowrap" data-testid={`${tid}-col-itr-type`}>ITR Type</th>
                <th className="px-4 py-3 font-bold whitespace-nowrap" data-testid={`${tid}-col-itr-value`}>ITR Value</th>
                <th className="w-32 px-4 py-3 font-bold whitespace-normal" data-testid={`${tid}-col-total-uvi`}>
                  <span className="block max-w-28 leading-snug">Total U/W Verified Income</span>
                </th>
                <th className="px-4 py-3 font-bold whitespace-nowrap" data-testid={`${tid}-col-trigger-event`}>Trigger Event</th>
                <th className="px-4 py-3 font-bold whitespace-nowrap" data-testid={`${tid}-col-max-rent`}>Max Rent</th>
                <th className="px-4 py-3 font-bold whitespace-nowrap" data-testid={`${tid}-col-date`}>Last Updated</th>
              </tr>
            </thead>
            <tbody data-testid={`${tid}-table-body`}>
              {rows.map((row, i) => (
                <tr
                  key={i}
                  className="border-b border-slate-100 last:border-b-0 odd:bg-white even:bg-slate-50/60"
                  data-testid={`${tid}-row-${i}`}
                >
                  <td className="px-4 py-3 align-top font-medium text-slate-900" data-testid={`${tid}-row-${i}-itr-type`}>
                    {formatMaxRentHistoryItrType(row.pmc_itr)}
                  </td>
                  <td className="px-4 py-3 align-top text-slate-900" data-testid={`${tid}-row-${i}-itr-value`}>
                    {formatMaxRentHistoryCell(row.itr_value)}
                  </td>
                  <td className="px-4 py-3 align-top text-slate-900" data-testid={`${tid}-row-${i}-total-uvi`}>
                    {formatMaxRentHistoryCell(row.total_uvi)}
                  </td>
                  <td className="px-4 py-3 align-top text-slate-900" data-testid={`${tid}-row-${i}-trigger-event`}>
                    {formatMaxRentHistoryCell(row.trigger_event)}
                  </td>
                  <td className="px-4 py-3 align-top font-semibold text-slate-900" data-testid={`${tid}-row-${i}-max-rent`}>{formatMaxRentColumn(row.maxrent)}</td>
                  <td className="px-4 py-3 align-top text-slate-700" data-testid={`${tid}-row-${i}-date`}>{formatMaxRentHistoryCell(row.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {lastPage > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3" data-testid={`${tid}-pagination`}>
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid={`${tid}-prev-btn`}
          >
            Prev
          </button>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600" data-testid={`${tid}-page-info`}>
            Page {page} of {lastPage}
          </span>
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => p + 1)}
            data-testid={`${tid}-next-btn`}
          >
            Next
          </button>
        </div>
      )}
    </OnScreenModal>
  )
}
